
import { Component, Vue } from "vue-property-decorator";
import FileUploadInput from "@/components/FileUploadInput.vue";
import InputWithLabel from "@/components/InputWithLabel.vue";
import { actions } from "@/survey";

interface SettingEntry {
  key: string;
  value: any;
}

interface SettingGroup {
  title: string;
  description: string;
  keys?: string[];
  prefix?: string;
}

const settingGroupDefinitions: SettingGroup[] = [
  {
    title: "Application",
    description: "General application display and web behavior settings.",
    keys: ["web.title", "web.max_db_rows"]
  },
  {
    title: "Microsoft OAuth / SSO",
    description: "Microsoft sign-in endpoints, credentials, redirect URI, and requested OAuth scopes.",
    prefix: "oauth.",
    keys: [
      "oauth.enabled",
      "oauth.server",
      "oauth.token_endpoint",
      "oauth.client_id",
      "oauth.client_secret",
      "oauth.redirect_uri",
      "oauth.scope"
    ]
  },
  {
    title: "Session And Token Security",
    description: "JWT signing, issuer, lifespan, and idle timeout settings.",
    keys: [
      "jwt.issuer",
      "jwt.secret",
      "jwt.idle_limit_seconds",
      "jwt.token_age_limit"
    ],
    prefix: "jwt."
  },
  {
    title: "Password Policy",
    description: "Local password complexity, expiration, and two-factor authentication rules. These do not apply to SSO-only accounts.",
    keys: [
      "survey.2fa_enabled",
      "survey.password_length",
      "survey.password_lc_required",
      "survey.password_uc_required",
      "survey.password_num_required",
      "survey.password_chars",
      "survey.password_max_days"
    ]
  },
  {
    title: "Maps",
    description: "Google Maps browser and server API keys.",
    prefix: "google."
  },
  {
    title: "Images",
    description: "Image resizing and output format settings.",
    prefix: "image."
  }
];

@Component({
  components: { InputWithLabel, FileUploadInput: FileUploadInput }
})
export default class AdminSettingsForm extends Vue {
  apiLocation = Vue.prototype.$apiLocation;
  client = Vue.prototype.$surveyClient;
  settings: any = null;
  trueFalseOptions = [
    { text: "True", value: "true" },
    { text: "False", value: "false" }
  ];

  valueUpdate(key: string, val: string) {
    // console.log(`${key} = ${val}`)
    Object.keys(this.settings).forEach((s) => {
      if (s === key) {
        this.settings[s].first = val;
      }
    });
  }

  get settingsArray(): SettingEntry[] {
    if (this.settings === null) {
      return [];
    }
    return Object.keys(this.settings).map((k) => {
      return { key: k, value: this.settings[k] };
    });
  }

  get settingGroups() {
    const settingsByKey = this.settingsArray.reduce((collector: { [key: string]: SettingEntry }, setting: SettingEntry) => {
      collector[setting.key] = setting;
      return collector;
    }, {});
    const usedKeys: { [key: string]: boolean } = {};
    const groups = settingGroupDefinitions.map((groupDefinition) => {
      const groupKeys = this.settingsArray
          .filter((setting) => this.settingBelongsToGroup(setting.key, groupDefinition))
          .map((setting) => setting.key);
      const orderedKeys = this.sortGroupKeys(groupKeys, groupDefinition.keys || []);
      orderedKeys.forEach((key) => usedKeys[key] = true);
      return {
        title: groupDefinition.title,
        description: groupDefinition.description,
        settings: orderedKeys.map((key) => settingsByKey[key])
      };
    }).filter((group) => group.settings.length > 0);
    const otherSettings = this.settingsArray.filter((setting) => !usedKeys[setting.key]);
    if (otherSettings.length > 0) {
      groups.push({
        title: "Other Settings",
        description: "Additional settings not assigned to a specific section.",
        settings: otherSettings.sort((a, b) => a.key.localeCompare(b.key))
      });
    }
    return groups;
  }

  settingBelongsToGroup(key: string, groupDefinition: SettingGroup) {
    return (groupDefinition.keys || []).indexOf(key) > -1 ||
        (groupDefinition.prefix !== undefined && key.indexOf(groupDefinition.prefix) === 0);
  }

  isTrueFalseSetting(setting: SettingEntry) {
    return setting.value.first === "true" || setting.value.first === "false";
  }

  sortGroupKeys(keys: string[], preferredOrder: string[]) {
    return keys.sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a);
      const bIndex = preferredOrder.indexOf(b);
      if (aIndex > -1 && bIndex > -1) {
        return aIndex - bIndex;
      }
      if (aIndex > -1) {
        return -1;
      }
      if (bIndex > -1) {
        return 1;
      }
      return a.localeCompare(b);
    });
  }

  async created() {
    this.settings = await this.client.getAdminConfig();
    // alert(JSON.stringify(this.settings))
  }

  async saveConfig() {
    this.settings = await this.client.saveAdminConfig(this.settings);
    this.$store.dispatch(actions.GET_CONFIG);
    alert("Saved settings");
  }
}
